import React, { useState } from 'react';
import { AppleLogo, GooglePlayLogo, LinkedinLogo } from '@phosphor-icons/react';
import { useMediaAsset } from '@hooks/use-media-asset';
import { trackCtaClick } from '@logging';
import {
  FooterBrandCol,
  FooterBrandDesc,
  FooterBrandLogo,
  FooterCol,
  FooterColLink,
  FooterColTitle,
  FooterCopyright,
  FooterCta,
  FooterCtaRow,
  FooterFollowButton,
  FooterPanel,
  FooterShell,
  FooterSocialLink,
  FooterWatermark,
  FooterWatermarkRow,
  FooterWatermarkText,
} from '@pages/landing-page.styles';
import { NewsletterOverlay } from './newsletter-overlay';
import { storeCtaProps } from './store-cta';

/*
 * The homepage's footer — a floating panel of links, then the watermark
 * with both stores — for every page built like the homepage. Its Follow
 * button opens the newsletter overlay itself, reporting the open with the
 * 'footer' placement it always had.
 */
export const SiteFooter: React.FC = () => {
  const { url: brandIconUrl } = useMediaAsset('house_shimmer_slate_icon_variation');
  const [newsletterOpen, setNewsletterOpen] = useState(false);
  const follow = () => {
    trackCtaClick('newsletter_open', 'footer');
    setNewsletterOpen(true);
  };

  return (
    <>
      <FooterShell>
        <FooterPanel>
          <FooterBrandCol>
            <FooterBrandLogo>
              {brandIconUrl && <img src={brandIconUrl} alt="" />}
              The Home CEO
            </FooterBrandLogo>
            <FooterBrandDesc>
              The Home CEO helps busy families run their households with clarity, calm, and the power of AI.
            </FooterBrandDesc>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '12px' }}>
              <FooterSocialLink
                href="https://www.linkedin.com/company/the-home-ceo/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow The Home CEO on LinkedIn"
                onClick={() => trackCtaClick('linkedin_company', 'footer')}
              >
                <LinkedinLogo size={20} />
              </FooterSocialLink>
              <FooterFollowButton type="button" onClick={follow}>
                Follow
              </FooterFollowButton>
            </div>
          </FooterBrandCol>

          <FooterCol>
            <FooterColTitle>Navigation</FooterColTitle>
            <FooterColLink to="/">Home</FooterColLink>
            <FooterColLink to="/ai-blogs">Blog</FooterColLink>
            <FooterColLink to="/info">About</FooterColLink>
            <FooterColLink to="/contact-us">Contact</FooterColLink>
          </FooterCol>

          <FooterCol>
            <FooterColTitle>Resources</FooterColTitle>
            <FooterColLink to="/ai-blogs">Articles</FooterColLink>
            <FooterColLink to="/press">Press</FooterColLink>
            <FooterColLink to="/info">FAQ</FooterColLink>
          </FooterCol>

          <FooterCol>
            <FooterColTitle>Legal</FooterColTitle>
            <FooterColLink to="/privacy-policy">Privacy Policy</FooterColLink>
            <FooterColLink to="/terms-of-service">Terms of Service</FooterColLink>
            <FooterColLink to="/data-use">Data Use</FooterColLink>
          </FooterCol>
        </FooterPanel>

        <FooterWatermark>
          <FooterWatermarkRow>
            {brandIconUrl && <img src={brandIconUrl} alt="" />}
            <FooterWatermarkText>The Home CEO</FooterWatermarkText>
          </FooterWatermarkRow>
          <FooterCtaRow>
            <FooterCta as="a" {...storeCtaProps('ios', 'footer')}>
              <AppleLogo weight="fill" /> App Store
            </FooterCta>
            <FooterCta as="a" {...storeCtaProps('android', 'footer')}>
              <GooglePlayLogo weight="fill" /> Google Play
            </FooterCta>
          </FooterCtaRow>
          <FooterCopyright>© 2026 Lat Long Labs. All rights reserved.</FooterCopyright>
        </FooterWatermark>
      </FooterShell>
      {newsletterOpen && <NewsletterOverlay onClose={() => setNewsletterOpen(false)} />}
    </>
  );
};
